/**
 * TaxSummaryReport — Financial Year Tax Summary
 *
 * Year parameter meaning:
 *   year = calendar year where FY starts
 *   year=2026 → April 2026–March 2027 → FY 2026–27
 *   year=2025 → April 2025–March 2026 → FY 2025–26
 *
 * Default: current calendar year (2026 in May 2026 → shows FY 2026-27)
 *
 * API: GET /api/v1/reports/tax-summary?year=2026
 */

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge, Spinner, Alert, Form, Button } from 'react-bootstrap';
import { FileText, Download, Shield } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import axiosInstance from '../../api/axiosConfig';

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTHS = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtINR = (n) =>
    `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtINRshort = (n) => {
    const v = parseFloat(n || 0);
    if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
    if (v >= 1000)   return `₹${(v / 1000).toFixed(0)}K`;
    return `₹${v.toFixed(0)}`;
};

const sum = (arr, key) => arr.reduce((s, r) => s + parseFloat(r[key] || 0), 0);

// year=2026 → "FY 2026–27"
const fyLabel = (y) => `FY ${y}–${String(parseInt(y) + 1).slice(-2)}`;

const defaultYear = () => new Date().getFullYear();

// "Apr '26", "May '26" etc.
const monthLabel = (m, y) => `${MONTHS[parseInt(m)] || '?'} '${String(y || '').slice(-2)}`;

// ── KPI card ──────────────────────────────────────────────────────────────────
const KPICard = ({ label, value, gradient, sub }) => (
    <Card className="border-0 text-white h-100" style={{ background: gradient, borderRadius: 12 }}>
        <Card.Body className="p-4">
            <div className="text-white-50 small fw-medium mb-1">{label}</div>
            <div style={{ fontSize: 32, fontWeight: 800 }}>{value}</div>
            {sub && <div className="text-white-50 mt-1" style={{ fontSize: 12 }}>{sub}</div>}
        </Card.Body>
    </Card>
);

// ════════════════════════════════════════════════════════════════════════════
const TaxSummaryReport = () => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);
    const [year,    setYear]    = useState(defaultYear());

    useEffect(() => {
        setLoading(true); setError(null); setData(null);
        axiosInstance.get('/reports/tax-summary', { params: { year } })
            .then(r  => setData(r.data.data))
            .catch(e => setError(e.response?.data?.message || 'Failed to load tax summary'))
            .finally(() => setLoading(false));
    }, [year]);

    const exportCSV = () => {
        const employees = data?.summary || [];
        if (!employees.length) return;
        const headers = ['Employee Code','Name','Department','Gross YTD','TDS YTD','PF YTD','PT YTD','Net YTD','Months Paid'];
        const rows = employees.map(e => [e.employee_code, e.employee_name, e.department || '',
            e.gross_ytd, e.tds_ytd, e.pf_ytd, e.pt_ytd, e.net_ytd, e.months_paid]);
        const csv = [headers, ...rows].map(r => r.join(',')).join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url; a.download = `tax_summary_${fyLabel(year)}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    const employees   = data?.summary || [];
    const monthlyData = (data?.monthly_tds || []).map(m => ({
        name: monthLabel(m.month, m.year || year),
        TDS:  parseFloat(m.tds   || 0),
    }));
    const totalTDS   = sum(employees, 'tds_ytd');
    const totalPF    = sum(employees, 'pf_ytd');
    const totalPT    = sum(employees, 'pt_ytd');
    const totalGross = sum(employees, 'gross_ytd');
    const totalNet   = sum(employees, 'net_ytd');
    const totalLiab  = totalTDS + totalPF + totalPT;
    const label      = fyLabel(year);

    return (
        <div className="p-3 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <FileText size={22} className="me-2 text-primary"/>Financial Year Tax Summary
                    </h4>
                    <p className="text-muted small mb-0">Statutory deductions summary — {label}</p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Select size="sm" style={{ width: 140 }} value={year}
                        onChange={e => setYear(parseInt(e.target.value))}>
                        {[2026, 2025, 2024, 2023].map(y => (
                            <option key={y} value={y}>{fyLabel(y)}</option>
                        ))}
                    </Form.Select>
                    <Button variant="outline-secondary" size="sm" onClick={exportCSV}
                        disabled={!employees.length || loading}>
                        <Download size={14} className="me-1"/>Export CSV
                    </Button>
                </div>
            </div>

            {loading && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary"/>
                    <p className="text-muted mt-2 small">Loading tax data…</p>
                </div>
            )}

            {error && !loading && <Alert variant="danger" className="small">{error}</Alert>}

            {!loading && !error && (
                <>
                    {/* KPI Cards */}
                    <Row className="g-3 mb-4">
                        <Col xs={12} md={4}>
                            <KPICard label="Total TDS Collected" value={fmtINR(totalTDS)}
                                gradient="linear-gradient(135deg,#1565C0,#1976D2)"
                                sub={`${employees.length} employees · ${label}`}/>
                        </Col>
                        <Col xs={12} md={4}>
                            <KPICard label="Total PF Contribution" value={fmtINR(totalPF)}
                                gradient="linear-gradient(135deg,#1B5E20,#2E7D32)"
                                sub="Employer + Employee share"/>
                        </Col>
                        <Col xs={12} md={4}>
                            <KPICard label="Total Professional Tax" value={fmtINR(totalPT)}
                                gradient="linear-gradient(135deg,#006064,#00ACC1)"
                                sub="State professional tax"/>
                        </Col>
                    </Row>

                    {/* Chart + Breakdown */}
                    <Row className="g-4 mb-4">
                        <Col lg={7}>
                            <Card className="border-0 shadow-sm h-100 p-4">
                                <div className="fw-bold mb-3">Collection Distribution</div>
                                {monthlyData.length === 0 ? (
                                    <div className="d-flex align-items-center justify-content-center
                                        text-muted" style={{ height: 200, fontSize: 13 }}>
                                        No payroll data for this financial year yet.
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={240}>
                                        <BarChart data={monthlyData}
                                            margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                            <XAxis dataKey="name" tick={{ fontSize: 12 }}/>
                                            <YAxis tickFormatter={fmtINRshort} tick={{ fontSize: 11 }}/>
                                            <Tooltip formatter={(v) => fmtINR(v)}/>
                                            <Legend/>
                                            <Bar dataKey="TDS" name="TDS Collected" fill="#1976D2" radius={[4,4,0,0]}/>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </Card>
                        </Col>
                        <Col lg={5}>
                            <Card className="border-0 shadow-sm h-100 p-4">
                                <div className="fw-bold mb-3">Statutory Breakdown</div>
                                <Table className="small mb-0">
                                    <thead>
                                        <tr className="text-muted" style={{ fontSize: 11 }}>
                                            <th>Component</th>
                                            <th className="text-end">Total Amount</th>
                                            <th className="text-end">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { label:'Income Tax (TDS)',     val:totalTDS, bg:'primary' },
                                            { label:'Provident Fund (PF)',   val:totalPF,  bg:'success' },
                                            { label:'Professional Tax (PT)', val:totalPT,  bg:'info'    },
                                            { label:'Total Payout (Net)',    val:totalNet, bg:null      },
                                        ].map((r, i) => (
                                            <tr key={i}>
                                                <td>{r.label}</td>
                                                <td className="text-end fw-medium">{fmtINR(r.val)}</td>
                                                <td className="text-end">
                                                    {r.bg
                                                        ? <Badge bg={r.bg} style={{ fontSize: 10 }}>Accrued</Badge>
                                                        : <span className="text-muted">—</span>}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="fw-bold border-top">
                                            <td>Total Statutory Liability</td>
                                            <td className="text-end">{fmtINR(totalLiab)}</td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </Table>
                                {employees.length > 0 && (
                                    <div className="mt-3 p-3 rounded" style={{ background:'#f0fdf4', fontSize:12 }}>
                                        <div className="d-flex justify-content-between">
                                            <span className="text-muted">Total Gross (YTD)</span>
                                            <span className="fw-bold">{fmtINR(totalGross)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mt-1">
                                            <span className="text-muted">Effective TDS Rate</span>
                                            <span className="fw-bold">
                                                {totalGross > 0
                                                    ? `${((totalTDS / totalGross) * 100).toFixed(1)}%`
                                                    : '0%'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </Col>
                    </Row>

                    {/* Employee-wise table */}
                    {employees.length > 0 ? (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white fw-bold border-0 pt-3 pb-2">
                                Employee-wise Tax Breakdown — {label}
                            </Card.Header>
                            <div style={{ overflowX:'auto' }}>
                                <Table hover className="mb-0 small align-middle">
                                    <thead className="bg-light">
                                        <tr className="text-muted text-uppercase" style={{ fontSize:11 }}>
                                            <th className="ps-4">Employee</th>
                                            <th>Dept</th>
                                            <th className="text-end">Gross YTD</th>
                                            <th className="text-end">TDS</th>
                                            <th className="text-end">PF</th>
                                            <th className="text-end">PT</th>
                                            <th className="text-end">Net YTD</th>
                                            <th className="text-end pe-4">Months</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {employees.map((e, i) => (
                                            <tr key={i}>
                                                <td className="ps-4">
                                                    <div className="fw-medium">{e.employee_name}</div>
                                                    <div className="text-muted" style={{ fontSize:10 }}>{e.employee_code}</div>
                                                </td>
                                                <td>{e.department || '—'}</td>
                                                <td className="text-end">{fmtINR(e.gross_ytd)}</td>
                                                <td className="text-end text-danger">{fmtINR(e.tds_ytd)}</td>
                                                <td className="text-end" style={{ color:'#d97706' }}>{fmtINR(e.pf_ytd)}</td>
                                                <td className="text-end text-info">{fmtINR(e.pt_ytd)}</td>
                                                <td className="text-end text-success fw-bold">{fmtINR(e.net_ytd)}</td>
                                                <td className="text-end pe-4"><Badge bg="light" text="dark">{e.months_paid}</Badge></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="fw-bold border-top" style={{ background:'#f8fafc' }}>
                                            <td className="ps-4" colSpan={2}>TOTAL</td>
                                            <td className="text-end">{fmtINR(totalGross)}</td>
                                            <td className="text-end text-danger">{fmtINR(totalTDS)}</td>
                                            <td className="text-end" style={{ color:'#d97706' }}>{fmtINR(totalPF)}</td>
                                            <td className="text-end text-info">{fmtINR(totalPT)}</td>
                                            <td className="text-end text-success">{fmtINR(totalNet)}</td>
                                            <td className="pe-4"></td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        </Card>
                    ) : (
                        <Card className="border-0 shadow-sm p-5 text-center text-muted">
                            <Shield size={40} className="mx-auto mb-2 opacity-40"/>
                            <p>No payroll data found for {label}.</p>
                            <p className="small">Run payroll for employees to see the tax summary.</p>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default TaxSummaryReport;
